import {
  Autocomplete,
  type AutocompleteProps,
  Chip,
  Grid2 as Grid,
  InputAdornment,
  InputBase,
  Stack,
  styled,
  Theme,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from "@mui/material";
import { ConditionType as ConditionTypeEnum } from "@sinch/core/entities/serverEnums";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import { useWatchDependency } from "@sinch/hooks/utilities/useWatchDependency";
import { MdsCloseXs, MdsSearch } from "@sinch/icons";
import type { ConditionCategory, ConditionType } from "@sinch/types/sinch.types";
import { difference, pluck, propEq } from "ramda";
import { ensureArray, isArray, isNotArray, isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import {
  ConditionSelectProps,
  type ConditionValue,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionInput";
import { RateConditionPossibleValues } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/queries.graphql";
import {
  RateConditionPossibleValuesQuery,
  RateConditionPossibleValuesQueryVariables,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/queries.types";
import { CheckboxInput } from "@sinch/components/form/CheckboxInput/CheckboxInput";

type NonArray<T> = T extends any[] ? never : T;

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#ffffff",
    color: "rgba(0, 0, 0, 0.87)",
    //maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));
export const ConditionalValueInputSelect = ({
  value,
  onChange,
  multiple,
  onOpenChange,
  subjectId,
  category,
  onBlur,
  subjectType,
  readOnly,
}: ConditionSelectProps<ConditionValue> & {
  subjectId: string | null;
  category: ConditionCategory;
  isNumberValue?: boolean;
  subjectType: ConditionType | ConditionTypeEnum;
}) => {
  const { t } = useTranslation();
  const { data } = useSuspenseQueryHandler<RateConditionPossibleValuesQuery, RateConditionPossibleValuesQueryVariables>(
    RateConditionPossibleValues,
    {
      subjectId: subjectId,
      category: category,
      type: subjectType,
    }
  );

  const options: string[] = useMemo(
    () => pluck("value")(data?.rateConditionPossibleValues || []),
    [data?.rateConditionPossibleValues]
  );

  useEffect(() => {
    if (value === null && isNotNil(options)) {
      onChange(options[0]);
    }
  }, [options]);

  const [isOpen, setIsOpen] = useState(false);

  const getOptionLabel = useCallback(
    (option: ConditionValue) => (data?.rateConditionPossibleValues || []).find(propEq("value", option))?.label ?? "",
    [options]
  );

  const isOptionNotExists = useMemo(
    () =>
      isNotNilOrEmpty(data?.rateConditionPossibleValues) &&
      isNotNilOrEmpty(value) &&
      !difference(ensureArray(value), pluck("value", data?.rateConditionPossibleValues ?? [])),
    [data]
  );

  const renderOption = useCallback<
    NonNullable<AutocompleteProps<NonArray<NonNullable<ConditionValue>>, boolean, boolean, false>["renderOption"]>
  >(
    (props, option, { selected }) => {
      const { key, ...optionProps } = props;
      return (
        <li key={key} {...optionProps}>
          {multiple && (
            <Grid alignItems="center" container>
              <Grid size="auto">
                <CheckboxInput color="primary" value={selected} />
              </Grid>
              <Grid size="grow">{getOptionLabel(option)}</Grid>
            </Grid>
          )}
          {!multiple && getOptionLabel(option)}
        </li>
      );
    },
    [getOptionLabel, multiple]
  );

  const renderTags = useCallback<
    NonNullable<AutocompleteProps<NonArray<NonNullable<ConditionValue>>, boolean, boolean, false>["renderTags"]>
  >(
    (vals, getTagProps) =>
      vals.map((val, index) => (
        <Chip
          {...getTagProps({ index })}
          key={String(val || index)}
          deleteIcon={<MdsIcon fontSize="xs" icon={MdsCloseXs} />}
          label={getOptionLabel(val)}
          sx={{ borderRadius: "4px !important" }}
        />
      )),
    [getOptionLabel]
  );

  useEffect(() => {
    if (multiple && isNotArray(value)) {
      onChange(ensureArray(value).filter((element) => isNotNil(element)));
    } else if (!multiple && isArray(value)) {
      onChange(null);
    }
  }, [multiple]);

  const [inputValue, setInputValue] = useState("");

  useWatchDependency(() => {
    setInputValue("");
  }, [value]);

  return (
    <Autocomplete<NonArray<NonNullable<ConditionValue>>, boolean, boolean, false>
      disableClearable
      disableCloseOnSelect={multiple}
      fullWidth
      getOptionLabel={getOptionLabel}
      inputValue={inputValue}
      multiple={multiple}
      onChange={(_, val) => {
        setInputValue("");
        onChange(val);
      }}
      onClose={() => {
        setIsOpen(false);
        onOpenChange(false);
      }}
      onInputChange={(event, newInputValue, reason) => {
        if (reason !== "reset" && reason !== "selectOption" && reason !== "removeOption") {
          // Ignore reset reason to prevent clearing
          setInputValue(newInputValue);
        }
      }}
      onOpen={() => {
        if (readOnly) {
          return;
        }
        setIsOpen(true);
        onOpenChange(true);
      }}
      options={options}
      readOnly={readOnly}
      renderInput={(params) => {
        const notOpen = isOptionNotExists
          ? t("valueNotExists")
          : ensureArray(value)
              .map(String)
              .map((element) => getOptionLabel(element))
              .join(", ");
        const inputFieldValue = isOpen ? params.inputProps.value : notOpen;
        return (
          <InputBase
            {...params.InputProps}
            endAdornment={
              <>
                {isNotNilOrEmpty(params.InputProps.startAdornment) ? (
                  <HtmlTooltip
                    disableFocusListener
                    title={
                      <Stack
                        data-cy="ConditionInput-valueInput-select-selectedDropdown"
                        direction="row"
                        flexWrap="wrap"
                        gap={1}
                      >
                        {params.InputProps.startAdornment}
                      </Stack>
                    }
                  >
                    <Chip
                      data-cy="ConditionInput-valueInput-select-selectedAdornment"
                      label={ensureArray(params.InputProps.startAdornment).length}
                      size="small"
                      sx={{ px: 0.5 }}
                    />
                  </HtmlTooltip>
                ) : undefined}
                {params.InputProps.endAdornment} {/* The "clear X" */}
              </>
            }
            fullWidth
            inputProps={{
              ...params.inputProps,
              value: inputFieldValue,
              onKeyDown: (event) => {
                if (event.key === "Backspace") {
                  event.stopPropagation();
                }
              },
              sx: isOptionNotExists
                ? (theme: Theme) => ({
                    color: theme.palette.warning.main,
                    fontWeight: "bold",
                  })
                : undefined,
              "data-cy": "ConditionInput-valueInput-select",
            }}
            onBlur={(e) => onBlur?.(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>)}
            startAdornment={
              isOpen && (
                <InputAdornment position="start">
                  <MdsIcon icon={MdsSearch} />
                </InputAdornment>
              )
            }
          />
        );
      }}
      renderOption={renderOption}
      renderTags={renderTags}
      slotProps={{
        popper: {
          "data-cy": "ConditionInput-valueInput-select-dropdown",
          style: { width: "fit-content" },
        },
      }}
      sx={{
        "&.MuiAutocomplete-hasPopupIcon>.MuiInputBase-root": {
          pl: 1,
          pr: "28px",
        },
      }}
      value={multiple ? ensureArray(value).filter((element) => isNotNil(element)) : value}
    />
  );
};
